import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CreditCard, Loader2 } from "lucide-react";

interface OnramperWidgetProps {
  depositAddress: string;
  userId: string;
  trigger?: React.ReactNode;
  onSuccess?: () => void;
}

const SITE_GREEN = "#2ebd85";

export const OnramperWidget = ({ 
  depositAddress, 
  userId: _userId,  // Reserved for future tracking
  trigger,
  onSuccess: _onSuccess  // Reserved for future callback
}: OnramperWidgetProps) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const apiKey = import.meta.env.VITE_ONRAMPER_API_KEY;
  
  // Build Onramper URL with parameters
  const buildOnramperUrl = () => {
    const url = new URL("https://widget.onramper.com");
    
    // Required parameters
    url.searchParams.set("apiKey", apiKey);
    url.searchParams.set("wallets", `USDC:${depositAddress}`);
    url.searchParams.set("onlyCryptos", "USDC");
    url.searchParams.set("onlyCryptoNetworks", "solana");
    url.searchParams.set("skipWalletScreen", "true");
    
    // Theme customization
    url.searchParams.set("darkMode", "true");
    url.searchParams.set("primaryColor", SITE_GREEN.replace("#", ""));
    
    // Optional: Default amount
    url.searchParams.set("defaultAmount", "100");
    
    return url.toString();
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button className="w-full" style={{ backgroundColor: SITE_GREEN }}>
            <CreditCard className="size-4 mr-2" />
            Buy USDC with Card
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[480px] h-[650px] p-0 overflow-hidden">
        <DialogTitle className="sr-only">Buy Crypto with Onramper</DialogTitle>
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-background">
            <Loader2 className="size-8 animate-spin" style={{ color: SITE_GREEN }} />
          </div>
        )}
        <iframe
          src={buildOnramperUrl()}
          title="Buy Crypto with Onramper"
          allow="payment *; camera *; microphone *"
          className="w-full h-full border-0 rounded-lg"
          onLoad={handleIframeLoad}
        />
      </DialogContent>
    </Dialog>
  );
};

